import { useMutation } from "@apollo/client";
import { ModalButtons, Textarea } from "components/ui/v2";
import { useDrawer } from "context/drawer/drawer.provider";
import { useModal } from "context/modal/modal.provider";
import { gqlEndorsement, gqlMessage } from "gql";
import {
  ICreateEndorsementInput,
  ICreateEndorsementResponse
} from "gql/Endorsement/mutations";
import useTranslation from "next-translate/useTranslation";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { EEndorsementTypeEnum } from "types/Endorsement.type";
import useSound from "use-sound";
import { getQueryOperator } from "utils/helpers";
import { LiquidSelectList } from "components/ui/v2";
import { Z_INDEX } from "components/ui/v2/z-index";

interface IEndorsementModalProps {
  userId: string;
  skillId: string;
  name: string;
}

const NewEndorsement = ({ userId, skillId, name: _name }: IEndorsementModalProps) => {
  const { closeDrawer } = useDrawer();
  const { closeModal } = useModal();
  const { t } = useTranslation("common");
  const [playComplete] = useSound("/sounds/feedback-sound.mp3", {
    volume: 0.25
  });
  const {
    handleSubmit: handleCreateNewEndorsement,
    control: endorsementControl,
    watch
  } = useForm<ICreateEndorsementInput>();

  const [createEndorsement, { loading: mutationLoading }] = useMutation<
    ICreateEndorsementResponse,
    { createEndorsementInput: ICreateEndorsementInput }
  >(gqlEndorsement.mutations.ADD_NEW_ENDORSEMENT, {
    refetchQueries: [
      getQueryOperator(gqlEndorsement.queries.GET_USER_ENDORSEMENTS_RESULTS),
      getQueryOperator(gqlMessage.queries.GET_ENDORSEMENTS)
    ],
    onCompleted() {
      toast.success(t("Gracias por tu validación 👌"));
      playComplete();
      closeDrawer();
      closeModal();
    },
    onError(e) {
      toast.error(e.message);
      closeDrawer();
      closeModal();
    }
  });

  const handleCancel = () => {
    closeDrawer();
    closeModal();
  };

  const handleCreate = async (data: ICreateEndorsementInput) => {
    try {
      createEndorsement({
        variables: {
          createEndorsementInput: {
            skilledUserId: userId,
            skillId: skillId,
            ...(() => {
              const {
                skilledUserId: _skilledUserId,
                skillId: _skillId,
                ...rest
              } = data;
              return rest;
            })()
          }
        }
      });
    } catch (error) {
      toast.error(
        t("Hubo un error al agregar su validación {{error}}", { error })
      );
    }
  };

  return (
    <form
      onSubmit={handleCreateNewEndorsement(handleCreate)}
      className="flex w-full flex-col gap-4 px-1 py-1"
    >
      <Controller
        name="type"
        control={endorsementControl}
        render={({ field }) => (
          <LiquidSelectList
            data={[
              {
                id: EEndorsementTypeEnum.SAME_PROJECT,
                name: t("Trabajamos en un proyecto juntos")
              },
              {
                id: EEndorsementTypeEnum.MEMBER_TO_LEADER,
                name: t("Fue mi líder en un proyecto")
              },
              {
                id: EEndorsementTypeEnum.LEADER_TO_MEMBER,
                name: t("Fuí su lider en un proyecto")
              }
            ]}
            labelKey="name"
            valueKey="id"
            placeholder={t("Seleccionar categoría")}
            className="w-full"
            value={field.value}
            onSelect={field.onChange}
            menuZIndex={Z_INDEX.MODAL + 100}
            variant="slate"
            size="sm"
            listSize="sm"
            collapsedShape="pill"
            reactive
          />
        )}
      />

      <Controller
        name="comment"
        control={endorsementControl}
        render={({ field }) => (
          <Textarea
            {...field}
            label={t("Comentario")}
            placeholder={t("Agrega un comentario a tu validación")}
            maxLength={200}
            rows={5}
          />
        )}
      />

      <span className="block text-right text-[11px] text-slate-500 dark:text-zinc-400">
        {watch("comment")?.toString().length || 0}/200
      </span>

      <ModalButtons
        loading={mutationLoading}
        cancelLabel={t("Cancelar")}
        submitLabel={t("Enviar")}
        onCancel={handleCancel}
      />
    </form>
  );
};

export default NewEndorsement;
